import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { RoutineRecentRunsRailCard } from '@heidi/agents';
import {
  RECENT_RUNS_LIMIT,
  RECENT_RUNS_RAIL_VISIBLE,
  runHasChatShell,
  sortRunsNewestFirst,
} from '@heidi/agents/data';
import { useAllWorkflowRuns, useWorkflowRuns } from '../hooks';
import { useOpenRunChat } from '../hooks/useOpenRunChat';
import { useRecentRunsRail } from '../hooks/useRecentRunsRail';

/**
 * The run chat's "Recent runs" card — a lightweight fixed-width list beside
 * the chat column. It is navigation, not work product, so it stays narrower
 * than the artifact canvas.
 *
 * Whole-card visibility — including the mutual exclusion with the artifact
 * canvas — belongs to {@link useRecentRunsRail}, shared with the header's
 * toggle. In ``standalone`` mode (the header popover on viewports too narrow
 * for the docked rail) the host's own open state is the authority instead, so
 * the rail's hidden-by-user preference and the artifact exclusion don't apply.
 * Presentation lives in {@link RoutineRecentRunsRailCard}.
 */
export function RoutineRecentRunsRail({
  workflowId,
  standalone = false,
  onRunOpen,
}: {
  workflowId: string;
  /** Hosted in its own surface (e.g. the header popover): skip the docked-rail open/closed state. */
  standalone?: boolean;
  /** Called when the user opens a run — lets a popover host close itself. */
  onRunOpen?: () => void;
}): JSX.Element | null {
  const router = useRouter();
  const openRunChat = useOpenRunChat();
  const { isOpen } = useRecentRunsRail(workflowId);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const rawRouteId = router.query.id;
  const selectedProjectId =
    (Array.isArray(rawRouteId) ? rawRouteId[0] : rawRouteId) ?? null;

  const { data: recentData } = useWorkflowRuns(workflowId, { limit: RECENT_RUNS_LIMIT });
  const { data: allData } = useAllWorkflowRuns(workflowId, { enabled: historyExpanded });

  const recentRuns = useMemo(
    () =>
      sortRunsNewestFirst(recentData?.runs ?? [])
        .filter(runHasChatShell)
        .slice(0, RECENT_RUNS_RAIL_VISIBLE),
    [recentData]
  );
  const allRuns = useMemo(
    () => sortRunsNewestFirst(allData?.runs ?? []).filter(runHasChatShell),
    [allData]
  );
  const hasMore = (recentData?.total ?? 0) > recentRuns.length;

  if ((!standalone && !isOpen) || recentRuns.length === 0) return null;

  return (
    <RoutineRecentRunsRailCard
      runs={recentRuns}
      allRuns={historyExpanded ? allRuns : undefined}
      hasMore={hasMore}
      selectedProjectId={selectedProjectId}
      historyExpanded={historyExpanded}
      onHistoryExpandedChange={setHistoryExpanded}
      standalone={standalone}
      onRunClick={(projectId) => {
        void openRunChat(projectId, { chatFirst: true });
        onRunOpen?.();
      }}
    />
  );
}
